import type { ComponentProps, Ref } from "react";

import { Heading as AriaHeading } from "react-aria-components";

import { Dialog, Modal, ModalOverlay } from "@/components/modals/modal";
import { Button } from "@/components/ui/buttons/button";
import { CloseButton } from "@/components/ui/buttons/close-button";
import { cx } from "@/utils/cx";

/** Static class map — Tailwind cannot detect dynamically built class names at build time. */
const FOOTER_ALIGN_CLASSES = {
	between: "sm:justify-between",
	center: "sm:justify-center",
	end: "sm:justify-end",
	justify: "sm:justify-between",
	start: "sm:justify-start",
	stretch: "sm:justify-between",
} as const satisfies Record<
	NonNullable<GeneralModalProps["footerAlign"]>,
	string
>;

interface GeneralModalProps {
	buttonSize?: ComponentProps<typeof Button>["size"];
	cancelColor?: "primary" | "secondary";
	cancelDisabled?: boolean;
	cancelText?: string;
	children?: React.ReactNode;
	/** When false, the modal will not auto-close after onSubmit is called. Defaults to true. */
	closeOnSubmit?: boolean;
	/** For scrollable body: focus management, scroll restoration, etc. */
	contentRef?: Ref<HTMLDivElement>;
	description?: React.ReactNode | string;
	footer?: React.ReactNode;
	footerAlign?: "between" | "center" | "end" | "justify" | "start" | "stretch";
	header?: React.ReactNode;
	isOpen: boolean;
	noHeaderBorder?: boolean;
	/** Optional; invoked after `onClose` when the user dismisses (overlay, close, cancel). */
	onCancel?: () => void;
	onClose: () => void;
	onSubmit: () => void;
	/** Merged onto the main panel (e.g. max width). */
	panelClassName?: string;
	/**
	 * Return false to keep the modal open when interacting with an outside
	 * element (e.g. Mapbox Address Autofill listbox portaled to document.body).
	 */
	shouldCloseOnInteractOutside?: (element: Element) => boolean;
	submitColor?: "primary" | "primary-destructive" | "secondary";
	submitDisabled?: boolean;
	submitText?: string;
	title: string;
}

export const GeneralModal = ({
	buttonSize,
	cancelColor = "secondary",
	cancelDisabled = false,
	cancelText = "Cancel",
	children,
	closeOnSubmit = true,
	contentRef,
	description,
	footer,
	footerAlign = "stretch",
	header,
	isOpen,
	noHeaderBorder = false,
	onCancel,
	onClose,
	onSubmit,
	panelClassName,
	shouldCloseOnInteractOutside,
	submitColor = "primary",
	submitDisabled = false,
	submitText,
	title,
}: GeneralModalProps) => {
	const handleClose = () => {
		onClose();
		onCancel?.();
	};

	const handleSubmit = () => {
		onSubmit();
		if (closeOnSubmit) {
			onClose();
		}
	};

	const footerClassName = cx(
		"z-10 flex w-full flex-col flex-col-reverse gap-3 border-t border-secondary px-4 pt-6 pb-4 sm:flex-row sm:px-6",
		FOOTER_ALIGN_CLASSES[footerAlign],
	);

	return (
		<ModalOverlay
			isDismissable
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleClose();
				}
			}}
			shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
		>
			<Modal isDismissable>
				<Dialog>
					<div
						className={cx(
							"pointer-events-auto relative flex h-screen max-h-full w-full flex-col overflow-hidden bg-primary shadow-xl sm:h-auto sm:max-h-[80vh] sm:max-w-120 sm:rounded-2xl",
							panelClassName,
						)}
					>
						<CloseButton
							className="absolute top-3 right-3 z-20"
							onClick={handleClose}
							size="lg"
							theme="light"
						/>

						<div
							className={cx(
								"flex flex-col gap-4 border-b border-secondary px-4 pt-5 pb-5 sm:px-6 sm:pt-6",
								noHeaderBorder && "border-b-0",
							)}
						>
							<div className="z-10 flex flex-col gap-0.5">
								{header ? (
									header
								) : (
									<>
										<AriaHeading
											className="text-md font-semibold text-primary capitalize"
											slot="title"
										>
											{title}
										</AriaHeading>

										{description && typeof description === "string" ? (
											<p className="text-sm text-tertiary">{description}</p>
										) : (
											description && description
										)}
									</>
								)}
							</div>
						</div>

						{children ? (
							<div
								className="scrollbar-hide flex flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6"
								ref={contentRef}
								style={{ WebkitOverflowScrolling: "touch" }}
							>
								{children}
							</div>
						) : (
							<div className="flex flex-1 flex-col"></div>
						)}

						<div className={footerClassName}>
							{footer ? (
								footer
							) : (
								<>
									<Button
										className={cx(
											"w-full",
											footerAlign !== "stretch" && "w-auto",
										)}
										color={cancelColor}
										isDisabled={cancelDisabled}
										onClick={handleClose}
										size={buttonSize}
									>
										{cancelText || "Cancel"}
									</Button>

									<Button
										className={cx(
											"w-full",
											footerAlign !== "stretch" && "w-auto",
										)}
										color={submitColor}
										isDisabled={submitDisabled}
										onClick={handleSubmit}
										size={buttonSize}
									>
										{submitText || "Save"}
									</Button>
								</>
							)}
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
};
